import { useEffect, useMemo, useState } from 'react'
import { getEffectiveTmdbApiKey, useUserSettings } from '../settings/useUserSettings'
import {
  fetchMovieDetails,
  type TmdbMovieDetails,
} from '../tmdb/tmdb'
import { LazyImage, useToast } from '../ui'
import { useWatchlists, type WatchlistEntry } from './useWatchlists'
import { useWriteWatchlists } from './useWriteWatchlists'
import { CreateWatchlistModal } from './CreateWatchlistModal'
import { WatchlistDetailView } from './WatchlistDetailView'
import { useBackButtonClose } from '../hooks/useBackButtonClose'

interface Props {
  onOpenDetail: (tmdbId: number, type: 'movie' | 'tv') => void
}

type ViewState =
  | { kind: 'list' }
  | { kind: 'detail'; id: string }

/**
 * Top-level watchlists tab — matches the desktop
 * `src/renderer/components/watchlist/WatchlistView.tsx` at 1:1 parity:
 *
 *  - Defaults to a card grid of all watchlists, each showing name,
 *    description, item count, total runtime, avg TMDB rating, and a
 *    2x2 poster mosaic of the first 4 entries.
 *  - "Create new watchlist" button in the header + empty-state CTA.
 *  - Clicking a card pushes local state into `detail` mode which
 *    mounts the full `WatchlistDetailView`.
 *  - Auto-forwards into the single list when the user only has one
 *    watchlist (mirrors desktop's WatchlistView effect).
 *
 * All reads go through `useWatchlists`; all writes go through
 * `useWriteWatchlists`. No global store state — navigation is kept in
 * local React state because the tab is a self-contained surface.
 */
export function WatchlistsTab({ onOpenDetail }: Props) {
  const { watchlists, loading } = useWatchlists()
  const { createWatchlist } = useWriteWatchlists()
  const { showSuccess } = useToast()
  const [view, setView] = useState<ViewState>({ kind: 'list' })
  const [isCreateOpen, setIsCreateOpen] = useState(false)

  // Browser back button dismisses the Create Watchlist modal.
  useBackButtonClose(isCreateOpen, () => setIsCreateOpen(false))

  // Auto-navigate to the only watchlist the user has, just like the
  // desktop view does for a cleaner first-time experience.
  useEffect(() => {
    if (!loading && watchlists.length === 1 && view.kind === 'list') {
      setView({ kind: 'detail', id: watchlists[0].id })
    }
  }, [loading, watchlists, view.kind])

  // If the watchlist we're viewing disappears (deleted elsewhere),
  // fall back to the list view instead of rendering an empty shell.
  useEffect(() => {
    if (view.kind !== 'detail') return
    if (!watchlists.some((w) => w.id === view.id)) {
      setView({ kind: 'list' })
    }
  }, [watchlists, view])

  const handleCreate = async (data: {
    name: string
    description?: string
    color?: string
  }) => {
    const trimmed = data.name.trim()
    const existingNames = new Set(
      watchlists.map((w) => w.name.trim().toLowerCase())
    )
    if (existingNames.has(trimmed.toLowerCase())) {
      throw new Error('A watchlist with that name already exists')
    }
    const id = await createWatchlist({
      name: trimmed,
      description: data.description,
      color: data.color,
    })
    if (id) {
      showSuccess(trimmed, { title: 'Watchlist created' })
      setView({ kind: 'detail', id })
    }
  }

  if (loading) {
    return <p className="py-12 text-center text-sm text-text-tertiary">Loading…</p>
  }

  if (view.kind === 'detail') {
    const selected = watchlists.find((w) => w.id === view.id)
    if (!selected) {
      // Shouldn't happen thanks to the effect above, but render a
      // safe fallback so the view never blanks out.
      return (
        <p className="py-12 text-center text-sm text-text-tertiary">
          Watchlist not found.
        </p>
      )
    }
    return (
      <WatchlistDetailView
        watchlist={selected}
        onBack={() => setView({ kind: 'list' })}
        onOpenDetail={onOpenDetail}
      />
    )
  }

  return (
    <div className="space-y-4">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-text-primary">Watchlists</h2>
          <p className="text-xs text-text-tertiary">
            Manage your saved movies and series.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setIsCreateOpen(true)}
          className="rounded-md bg-accent-primary px-3 py-1.5 text-xs font-semibold text-black transition-colors hover:bg-accent-hover"
        >
          + New watchlist
        </button>
      </header>

      {watchlists.length === 0 ? (
        <EmptyState onCreate={() => setIsCreateOpen(true)} />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
          {watchlists.map((wl) => (
            <WatchlistCard
              key={wl.id}
              watchlist={wl}
              onOpen={() => setView({ kind: 'detail', id: wl.id })}
            />
          ))}
          <button
            type="button"
            onClick={() => setIsCreateOpen(true)}
            className="flex min-h-[220px] flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border-primary bg-bg-secondary/40 p-6 text-sm text-text-secondary transition-colors hover:border-accent-primary/40 hover:bg-bg-secondary hover:text-text-primary"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full border border-border-primary text-2xl">
              +
            </span>
            Create new watchlist
          </button>
        </div>
      )}

      <CreateWatchlistModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreateWatchlist={handleCreate}
      />
    </div>
  )
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border-primary bg-bg-secondary/40 py-16 text-center">
      <svg
        className="h-12 w-12 opacity-40"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M19 11H5m14 0a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2m14 0V9a2 2 0 0 0-2-2M5 11V9a2 2 0 0 1 2-2m0 0V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2M7 7h10" />
      </svg>
      <p className="text-base font-medium text-text-primary">No watchlists yet</p>
      <p className="text-sm text-text-tertiary">
        Create your first watchlist to start organising movies and series.
      </p>
      <button
        type="button"
        onClick={onCreate}
        className="mt-2 rounded-md bg-accent-primary px-4 py-2 text-xs font-semibold text-black hover:bg-accent-hover"
      >
        Create watchlist
      </button>
    </div>
  )
}

function WatchlistCard({
  watchlist,
  onOpen,
}: {
  watchlist: WatchlistEntry
  onOpen: () => void
}) {
  const tmdbIds = useMemo(
    () =>
      watchlist.movies
        .slice(0, 4)
        .map((m) => m.tmdbId)
        .filter((id): id is number => typeof id === 'number'),
    [watchlist.movies]
  )
  const tmdbIndex = useEagerMovieIndex(tmdbIds)

  const stats = useMemo(() => {
    let totalRuntime = 0
    let ratingSum = 0
    let ratingCount = 0
    for (const m of watchlist.movies) {
      if (m.tmdbId == null) continue
      const tmdb = tmdbIndex.get(m.tmdbId)
      if (!tmdb) continue
      if (tmdb.runtime) totalRuntime += tmdb.runtime
      if (tmdb.rating > 0) {
        ratingSum += tmdb.rating
        ratingCount++
      }
    }
    return {
      total: watchlist.movies.length,
      totalRuntime,
      avgRating: ratingCount > 0 ? ratingSum / ratingCount : 0,
    }
  }, [watchlist.movies, tmdbIndex])

  const collage: Array<string | null> = useMemo(() => {
    const slots: Array<string | null> = []
    for (const m of watchlist.movies.slice(0, 4)) {
      const tmdb = m.tmdbId != null ? tmdbIndex.get(m.tmdbId) : undefined
      const external = normalizePosterPath(m.externalPosterPath)
      slots.push(tmdb?.posterUrl ?? external ?? null)
    }
    return slots
  }, [watchlist.movies, tmdbIndex])

  return (
    <button
      type="button"
      onClick={onOpen}
      className="group flex flex-col overflow-hidden rounded-xl border border-border-primary bg-bg-secondary text-left shadow-cinematic transition-all duration-300 hover:-translate-y-0.5 hover:border-accent-primary/40 hover:shadow-cinematic-lg"
    >
      <CollageCover posters={collage} total={watchlist.movies.length} />

      <div className="flex flex-1 flex-col gap-2 p-3">
        <div className="flex items-center gap-2">
          {watchlist.color && (
            <span
              className="inline-block h-3 w-3 shrink-0 rounded-full ring-2 ring-white/10"
              style={{ backgroundColor: watchlist.color }}
            />
          )}
          <h3 className="truncate text-sm font-bold text-text-primary group-hover:text-accent-primary">
            {watchlist.name}
          </h3>
          {watchlist.isDefault && (
            <span className="rounded bg-bg-tertiary px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
              Default
            </span>
          )}
        </div>

        {watchlist.description && (
          <p className="line-clamp-2 text-xs text-text-secondary">
            {watchlist.description}
          </p>
        )}

        <div className="mt-auto flex flex-wrap items-baseline gap-x-3 gap-y-1 text-[11px] text-text-tertiary">
          <span>
            <span className="font-semibold text-text-primary">{stats.total}</span>{' '}
            {stats.total === 1 ? 'item' : 'items'}
          </span>
          {stats.totalRuntime > 0 && (
            <span>
              <span className="font-semibold text-text-primary">
                {formatHours(stats.totalRuntime)}
              </span>
            </span>
          )}
          {stats.avgRating > 0 && (
            <span>
              <span className="font-semibold text-blue-300">
                ★ {stats.avgRating.toFixed(1)}
              </span>
            </span>
          )}
        </div>
      </div>
    </button>
  )
}

/**
 * 2x2 poster mosaic, with placeholder cells for empty slots. Mirrors
 * the desktop card's behavior:
 *   0 items → single placeholder
 *   1 item  → one full-width poster
 *   2 items → two side-by-side posters
 *   3 items → three posters with the fourth cell as a placeholder
 *   4+      → full 2x2
 */
function CollageCover({
  posters,
  total,
}: {
  posters: Array<string | null>
  total: number
}) {
  if (total === 0) {
    return (
      <div className="flex aspect-[16/10] w-full items-center justify-center bg-bg-tertiary text-4xl opacity-40">
        🎬
      </div>
    )
  }
  if (total === 1) {
    return (
      <div className="aspect-[16/10] w-full overflow-hidden bg-bg-tertiary">
        <CollagePoster poster={posters[0] ?? null} />
      </div>
    )
  }
  if (total === 2) {
    return (
      <div className="grid aspect-[16/10] w-full grid-cols-2 gap-0.5 bg-bg-tertiary">
        <CollagePoster poster={posters[0] ?? null} />
        <CollagePoster poster={posters[1] ?? null} />
      </div>
    )
  }
  return (
    <div className="relative grid aspect-[16/10] w-full grid-cols-2 grid-rows-2 gap-0.5 bg-bg-tertiary">
      <CollagePoster poster={posters[0] ?? null} />
      <CollagePoster poster={posters[1] ?? null} />
      <CollagePoster poster={posters[2] ?? null} />
      <CollagePoster poster={posters[3] ?? null} />
      {total > 4 && (
        <div className="pointer-events-none absolute bottom-1 right-1 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-semibold text-white">
          +{total - 4}
        </div>
      )}
    </div>
  )
}

function CollagePoster({ poster }: { poster: string | null }) {
  if (!poster) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-bg-secondary text-lg opacity-30">
        🎬
      </div>
    )
  }
  return (
    <LazyImage
      src={poster}
      alt=""
      className="h-full w-full"
      placeholderColor="#1a1a1a"
      fallback={
        <div className="flex h-full w-full items-center justify-center bg-bg-secondary text-lg opacity-30">
          🎬
        </div>
      }
    />
  )
}

function useEagerMovieIndex(
  tmdbIds: number[]
): Map<number, TmdbMovieDetails> {
  const { settings } = useUserSettings()
  const [index, setIndex] = useState<Map<number, TmdbMovieDetails>>(
    () => new Map()
  )

  useEffect(() => {
    // Falls back to the bundled developer key when the user hasn't supplied one.
    const apiKey = getEffectiveTmdbApiKey(settings)
    if (!apiKey || tmdbIds.length === 0) return
    const language = settings?.tmdbPreferredLanguage || 'en'
    let cancelled = false

    const pending: Promise<void>[] = []
    for (const id of tmdbIds) {
      pending.push(
        fetchMovieDetails(id, apiKey, language).then((d) => {
          if (cancelled || !d) return
          setIndex((prev) => {
            if (prev.has(id)) return prev
            const next = new Map(prev)
            next.set(id, d)
            return next
          })
        })
      )
    }
    void Promise.all(pending)

    return () => {
      cancelled = true
    }
  }, [tmdbIds, settings?.tmdbApiKey, settings?.tmdbPreferredLanguage])

  return index
}

function formatHours(minutes: number): string {
  if (minutes <= 0) return ''
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h === 0) return `${m}m`
  if (m === 0) return `${h}h`
  return `${h}h ${m}m`
}

function normalizePosterPath(raw: string | null): string | null {
  if (!raw) return null
  if (/^https?:\/\//i.test(raw)) return raw
  return `https://image.tmdb.org/t/p/w500${raw.startsWith('/') ? raw : `/${raw}`}`
}
